import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VALID_TIME_RANGES } from '../utils/dashboard/constants';

/**
 * Parse a comma-separated string of IDs into an array of numbers
 * @param {string} value - Comma-separated string
 * @returns {number[]} Array of numeric IDs
 */
const parseNumericList = (value) => {
    if (!value) return [];
    return value
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((num) => !Number.isNaN(num));
};

/**
 * Parse a comma-separated string into an array of strings
 * @param {string} value - Comma-separated string
 * @returns {string[]} Array of strings
 */
const parseStringList = (value) => {
    if (!value) return null; // Return null to distinguish from "explicitly empty"
    return value.split(',').map((s) => s.trim()).filter(Boolean);
};

/**
 * Report filter URL parameter names
 */
export const REPORT_FILTER_PARAMS = [
    'hosts',
    'metrics',
    'business_services',
    'host_groups',
    'host_categories',
    'metric_categories',
    'time_range',
    'time_from',
    'time_to',
    'host_statuses',
    'metric_statuses',
    'embed', // Embed mode - shows only dashboard grid and action bar
];

/**
 * Hook to read report filters from URL query parameters (one-way: URL → state)
 * Returns filters in the format expected by ReportFiltersConfiguration and DashboardViewer
 *
 * @returns {Object|null} Filters object or null if no filters in URL
 *
 * @example
 * // URL: /reports/123/view?business_services=5&time_range=last_7d
 * const filters = useReportUrlFilters();
 * // Returns: { entities: { business_services: [5], ... }, timeRange: { mode: 'predefined', range: 'last_7d' } }
 */
export const useReportUrlFilters = () => {
    const [searchParams] = useSearchParams();

    return useMemo(() => {
        // Check if any filter params exist
        const hasFilterParams = REPORT_FILTER_PARAMS.some((param) => searchParams.has(param));
        if (!hasFilterParams) {
            return null;
        }

        // Parse entity filters (numeric IDs)
        const hosts = parseNumericList(searchParams.get('hosts'));
        const metrics = parseNumericList(searchParams.get('metrics'));
        const businessServices = parseNumericList(searchParams.get('business_services'));
        const hostGroups = parseNumericList(searchParams.get('host_groups'));

        // Parse category filters (cascade filters - filter available hosts/metrics)
        const hostCategories = parseNumericList(searchParams.get('host_categories'));
        const metricCategories = parseNumericList(searchParams.get('metric_categories'));

        // Parse time range
        let timeRange = null;
        const timeRangeParam = searchParams.get('time_range');
        const timeFrom = searchParams.get('time_from');
        const timeTo = searchParams.get('time_to');

        if (timeRangeParam && VALID_TIME_RANGES.includes(timeRangeParam)) {
            timeRange = {
                mode: 'predefined',
                range: timeRangeParam,
                start: null,
                end: null,
                timezone: 'local',
            };
        } else if (timeFrom && timeTo) {
            timeRange = {
                mode: 'custom',
                range: null,
                start: timeFrom,
                end: timeTo,
                timezone: 'local',
            };
        }

        // Parse status filters - only include if explicitly provided
        const hostStatuses = parseStringList(searchParams.get('host_statuses'));
        const metricStatuses = parseStringList(searchParams.get('metric_statuses'));

        const result = {
            entities: {
                hosts,
                metrics,
                business_services: businessServices,
                host_groups: hostGroups,
            },
            timeRange,
        };

        // Add category filters if provided (outside of entities, like hostStatuses/metricStatuses)
        if (hostCategories.length > 0) {
            result.hostCategories = hostCategories;
        }
        if (metricCategories.length > 0) {
            result.metricCategories = metricCategories;
        }

        // Only include status filters if they were explicitly provided in URL
        if (hostStatuses) {
            result.hostStatuses = hostStatuses;
        }
        if (metricStatuses) {
            result.metricStatuses = metricStatuses;
        }

        // Check for embed mode
        const embedParam = searchParams.get('embed');
        if (embedParam === 'true' || embedParam === '1') {
            result.embedMode = true;
        }

        return result;
    }, [searchParams]);
};

export default useReportUrlFilters;

